<script setup>
import { ref, reactive, computed, onMounted, onActivated } from 'vue'
import api from '@/services/api'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import { useAuthStore } from '@/stores/auth'
import { usePaginacao } from '@/composables/usePaginacao'
import { useUI } from '@/composables/useUI'

const emit = defineEmits(['adicionar-item', 'editar-item', 'excluir-item'])

const authStore = useAuthStore()
const { obterBadgeNeutro } = useUI()

const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao(10)

const regras = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)

const sortField = ref('')
const sortOrder = ref('')

const filtros = reactive({
  titulo: '',
  modulo: '',
  chave: '',
  descricao: '',
  isGrupo: '',
  isHeader: '',
  isRegraParaUsuario: ''
})

const temPermissaoCompleta = computed(() => authStore.pode('REGRAS_DE_ACESSO_COMPLETO'))
const temPermissaoVisualizacao = computed(() => authStore.pode('REGRAS_DE_ACESSO_VISUALIZACAO'))

const podeVerAcoes = computed(() => temPermissaoCompleta.value || temPermissaoVisualizacao.value)

const colunas = computed(() => {
  const cols = [
    { field: 'idRegraAcesso', label: 'ID', width: 100, sortable: true, slotName: 'id' },
    { field: 'titulo', label: 'Título Menu', sortable: true, slotName: 'titulo' },
    { field: 'modulo', label: 'Módulo', sortable: true, class: 'fw-medium' },
    { field: 'descricao', label: 'Descrição', sortable: true },
    { field: 'isGrupo', label: 'É Grupo', align: 'center', sortable: true, slotName: 'isGrupo' },
    { field: 'isHeader', label: 'É Header', align: 'center', sortable: true, slotName: 'isHeader' },
    { field: 'isRegraParaUsuario', label: 'Liberado Cliente', align: 'center', sortable: true, slotName: 'isRegraParaUsuario' },
    { field: 'ordem', label: 'Ordem', align: 'center', sortable: true, width: 90 },
    { field: 'contemPermissoes', label: 'Permissões', align: 'center', sortable: false, slotName: 'contemPermissoes' }
  ]
  if (podeVerAcoes.value) {
    cols.push({ field: 'acoes', label: 'Ações', width: 120, align: 'right', slotName: 'acoes', resizable: false, sortable: false })
  }
  return cols
})

const sortResolver = (field, _order, _data) => {
  if (!field.startsWith('lst')) return null
  return null
}

const handleSortChange = () => {
  buscarDados(1)
}

const obterTextoBooleano = (valor) => (valor ? 'Sim' : 'Não')
const obterClasseBooleano = (valor) => (valor ? 'badge badge-ghost-success' : 'badge badge-ghost-light')
const contemPermissoes = (regra) => (regra.permissoes && regra.permissoes.length > 0)

const buscarDados = async (pageNumber = 1) => {
  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)

    if (filtros.titulo) params.append('titulo', filtros.titulo)
    if (filtros.modulo) params.append('modulo', filtros.modulo)
    if (filtros.chave) params.append('chave', filtros.chave)
    if (filtros.descricao) params.append('descricao', filtros.descricao)
    if (filtros.isGrupo !== '') params.append('isGrupo', filtros.isGrupo)
    if (filtros.isHeader !== '') params.append('isHeader', filtros.isHeader)
    if (filtros.isRegraParaUsuario !== '') params.append('isRegraParaUsuario', filtros.isRegraParaUsuario)
    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)

    const response = await api.get(`/RegrasAcesso?${params.toString()}`)
    regras.value = response.data
    atualizarPaginacao(response.headers)
  } catch (error) {
    console.error('Erro ao buscar regras de acesso:', error)
    erro.value = 'Não foi possível carregar os dados das regras de acesso.'
    regras.value = []
    paginacao.value.TotalCount = 0
  } finally {
    loading.value = false
  }
}

const aplicarFiltros = () => { buscarDados(1) }
const limparFiltros = () => {
  Object.keys(filtros).forEach(k => { filtros[k] = '' })
  buscarDados(1)
}

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) buscarDados(page)
}

const mudarTamanhoPagina = (novoTamanho) => {
  paginacao.value.PageSize = novoTamanho
  buscarDados(1)
}

const adicionarRegra = () => { emit('adicionar-item') }
const editarRegra = (regra) => { emit('editar-item', regra) }
const excluirRegra = (regra) => { emit('excluir-item', { ...regra, name: regra.modulo + ' - ' + regra.chave }) }

onMounted(() => { buscarDados(1) })
onActivated(() => { buscarDados(paginacao.value.CurrentPage || 1) })

defineExpose({ buscarDados })
</script>

<template>
  <PaginaAdministrar
    titulo="Regras de Acesso"
    subtitulo="Listagem e gerenciamento de registos"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @adicionar="adicionarRegra"
    @atualizar="aplicarFiltros"
    :mostrar-botao-adicionar="temPermissaoCompleta"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i>
        <span>Filtros</span>
      </button>
    </template>

    <template #filtros>
      <h3 class="filtros-titulo">Filtrar por:</h3>
      <div class="row g-3 align-items-end">
        <div class="col-md-3">
          <label for="filtro-titulo" class="form-label">Título Menu</label>
          <input type="text" id="filtro-titulo" class="form-control" v-model="filtros.titulo" placeholder="Buscar pelo título do menu..." @keydown.enter.prevent="aplicarFiltros">
        </div>
        <div class="col-md-3">
          <label for="filtro-modulo" class="form-label">Módulo</label>
          <input type="text" id="filtro-modulo" class="form-control" v-model="filtros.modulo" @keydown.enter.prevent="aplicarFiltros">
        </div>
        <div class="col-md-3">
          <label for="filtro-chave" class="form-label">Chave</label>
          <input type="text" id="filtro-chave" class="form-control" v-model="filtros.chave" @keydown.enter.prevent="aplicarFiltros">
        </div>
        <div class="col-md-3">
          <label for="filtro-descricao" class="form-label">Descrição</label>
          <input type="text" id="filtro-descricao" class="form-control" v-model="filtros.descricao" @keydown.enter.prevent="aplicarFiltros">
        </div>
        <div class="col-md-3">
          <label for="filtro-is-grupo" class="form-label">É Grupo?</label>
          <select id="filtro-is-grupo" class="form-select" v-model="filtros.isGrupo" @change="aplicarFiltros">
            <option :value="''">Todos</option>
            <option :value="true">Sim</option>
            <option :value="false">Não</option>
          </select>
        </div>
        <div class="col-md-3">
          <label for="filtro-is-header" class="form-label">É Header?</label>
          <select id="filtro-is-header" class="form-select" v-model="filtros.isHeader" @change="aplicarFiltros">
            <option :value="''">Todos</option>
            <option :value="true">Sim</option>
            <option :value="false">Não</option>
          </select>
        </div>
        <div class="col-md-3">
          <label for="filtro-is-regra-para-usuario" class="form-label">Liberado Cliente?</label>
          <select id="filtro-is-regra-para-usuario" class="form-select" v-model="filtros.isRegraParaUsuario" @change="aplicarFiltros">
            <option :value="''">Todos</option>
            <option :value="true">Sim</option>
            <option :value="false">Não</option>
          </select>
        </div>

        <div class="col-auto">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros">Aplicar Filtros</button>
        </div>
      </div>
    </template>

    <div v-if="loading" class="text-center p-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">A carregar...</span>
      </div>
    </div>

    <div v-else-if="erro" class="alert alert-danger m-3 border-0 shadow-sm">
      <i class="bi bi-info-circle-fill me-2"></i> {{ erro }}
    </div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="regras"
      :loading="loading"
      empty-message="Nenhum registo encontrado."
      row-key="idRegraAcesso"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      @sort-change="handleSortChange"
      @row-dblclick="editarRegra($event.row)"
    >
      <template #id="{ row }">
        <span class="text-muted fw-medium">#{{ row.idRegraAcesso }}</span>
      </template>
      <template #titulo="{ row }">
        <span v-if="row.titulo" :class="obterBadgeNeutro()">{{ row.titulo }}</span>
        <span v-else class="text-muted">—</span>
      </template>

      <template #isGrupo="{ row }">
        <span class="badge" :class="obterClasseBooleano(row.isGrupo)">{{ obterTextoBooleano(row.isGrupo) }}</span>
      </template>
      <template #isHeader="{ row }">
        <span class="badge" :class="obterClasseBooleano(row.isHeader)">{{ obterTextoBooleano(row.isHeader) }}</span>
      </template>
      <template #isRegraParaUsuario="{ row }">
        <span class="badge" :class="obterClasseBooleano(row.isRegraParaUsuario)">{{ obterTextoBooleano(row.isRegraParaUsuario) }}</span>
      </template>
      <template #contemPermissoes="{ row }">
        <span class="badge" :class="obterClasseBooleano(contemPermissoes(row))">{{ obterTextoBooleano(contemPermissoes(row)) }}</span>
      </template>

      <template #acoes="{ row }">
        <div class="acoes-wrapper" v-if="podeVerAcoes">
          <button class="btn-icon text-primary" title="Editar" @click="editarRegra(row)">
            <i class="bi bi-pencil-fill"></i>
          </button>
          <button class="btn-icon" title="Excluir" @click="excluirRegra(row)">
            <i class="bi bi-trash-fill text-danger"></i>
          </button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>
</template>

<style scoped>
.filtros-titulo {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--bs-secondary-color);
  margin-bottom: 1rem;
}
</style>
